import interact from "interactjs";
import IdealStudio from "../../../../../core/editor/main";
import getMediaBoxSize from "../../../../utils/getMediaBoxSize";

// Makes `.palette-item` cards draggable onto the canvas: a card ghost follows the
// cursor off-canvas; over the canvas it becomes a real-size drop preview whose
// geometry mirrors the EditorCanvasSli dropzone so the preview matches the drop.

const GHOST_ID = "palette-drag-ghost";
const PREVIEW_ID = "palette-drop-preview";
let bound = false;
let session = null;

const el = (id) => document.getElementById(id);

const removeEl = (id) => {
  const node = el(id);
  if (node && node.parentElement) node.parentElement.removeChild(node);
};

const pctNum = (value, fallback) => {
  const n = parseFloat(value);
  return isNaN(n) ? fallback : n;
};

// Loading overlay for the video footprint (reuses .cmp-cell__spinner).
const buildSpinner = () => {
  const wrap = document.createElement("div");
  Object.assign(wrap.style, {
    position: "absolute",
    inset: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.4)",
  });
  const sp = document.createElement("span");
  sp.className = "cmp-cell__spinner";
  wrap.appendChild(sp);
  return wrap;
};

// Visible slide canvas under the pointer (normal or contained view).
const canvasUnder = (clientX, clientY) => {
  for (const id of ["contained_maincontent", "maincontent"]) {
    const node = el(id);
    if (!node) continue;
    const rect = node.getBoundingClientRect();
    if (
      rect.width > 0 &&
      rect.height > 0 &&
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    ) {
      return { node, rect };
    }
  }
  return null;
};

// Resolve plugin + box size (% of canvas) from the card's data-* attributes.
// Image/video sizes load async (natural ratio); others use the plugin default.
const resolveItem = (target) => {
  const pluginName = target.getAttribute("data-plugin") || target.getAttribute("name");
  const url = target.getAttribute("data-url");
  const media = target.getAttribute("data-media");

  let wPct = 25;
  let hPct = 30;
  try {
    const cfg = IdealStudio.Plugins.get(pluginName)?.getConfig?.();
    if (cfg) {
      wPct = pctNum(cfg.initialWidthSlide || cfg.initialWidth, wPct);
      hPct = pctNum(cfg.initialHeightSlide || cfg.initialHeight, hPct);
    }
  } catch {
    /* keep defaults */
  }
  // Per-item size (e.g. each Şekiller shape has its own default box).
  wPct = pctNum(target.getAttribute("data-width"), wPct);
  hPct = pctNum(target.getAttribute("data-height"), hPct);

  const item = { url, media, wPct, hPct };

  const dragSrc = target.querySelector(".cmp-cell__dragsrc");
  const realHTML = dragSrc && dragSrc.innerHTML.trim() ? dragSrc.innerHTML : "";
  const frame = target.querySelector(".cmp-cell__frame");
  if (realHTML) {
    item.realPreview = true;
    item.previewHTML = realHTML;
    // Plugin renders (image/video/shape/table/chart) are captured at a fixed
    // natural size and scaled to fit; text/smart auto-size to the canvas font.
    if (target.getAttribute("data-preview-real-scale") === "1") {
      item.scaleReal = true;
      item.natW = dragSrc.offsetWidth || 300;
      item.natH = dragSrc.offsetHeight || 180;
    }
  } else {
    item.schematic = target.getAttribute("data-schematic") === "1";
    item.previewHTML = frame ? frame.innerHTML : "";
  }

  if (url && (media === "image" || media === "video")) {
    getMediaBoxSize(url, media)
      .then((size) => {
        if (session && session.item === item) {
          item.wPct = pctNum(size.width, wPct);
          item.hPct = pctNum(size.height, hPct);
        }
      })
      .catch(() => {});
  }

  return item;
};

const buildPreview = (item) => {
  const node = document.createElement("div");
  node.setAttribute("id", PREVIEW_ID);
  Object.assign(node.style, {
    position: "fixed",
    boxSizing: "border-box",
    border: "2px solid #7f56d9",
    background: "rgba(127,86,217,0.12)",
    borderRadius: "6px",
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: "100000",
    display: "none",
    boxShadow: "0 8px 24px rgba(127,86,217,0.25)",
  });
  if (item.url && item.media === "image") {
    const img = document.createElement("img");
    img.src = item.url;
    Object.assign(img.style, {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      opacity: "0.65",
      display: "block",
    });
    node.appendChild(img);
  } else if (item.url && item.media === "video" && !/<img/i.test(item.previewHTML || "")) {
    // Thumbnail-less video files: load the real clip (placeholder + spinner until
    // the first frame; placeholder stays if it can't play inline).
    if (item.previewHTML) {
      const bg = document.createElement("div");
      Object.assign(bg.style, { position: "absolute", inset: "0", overflow: "hidden" });
      bg.innerHTML = item.previewHTML;
      node.appendChild(bg);
    }
    const video = document.createElement("video");
    video.src = item.url;
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    Object.assign(video.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "none",
    });
    const spinner = buildSpinner();
    const clearSpinner = () => {
      if (spinner.parentElement) spinner.parentElement.removeChild(spinner);
    };
    video.addEventListener("loadeddata", () => {
      try {
        video.currentTime = 0.1; // nudge off the black first frame
      } catch {
        /* ignore */
      }
      video.style.display = "block";
      clearSpinner();
    });
    video.addEventListener("error", () => {
      if (video.parentElement) video.parentElement.removeChild(video);
      clearSpinner();
    });
    node.appendChild(video);
    node.appendChild(spinner);
  } else if (item.realPreview && !item.scaleReal && item.previewHTML) {
    // Text/smart real render — sized to true on-canvas pixels in onmove.
    const inner = document.createElement("div");
    Object.assign(inner.style, {
      position: "absolute",
      top: "0",
      left: "0",
      overflow: "hidden",
      opacity: "0.95",
    });
    inner.innerHTML = item.previewHTML;
    node.appendChild(inner);
    node._realInner = inner;
  } else if ((item.scaleReal || item.schematic) && item.previewHTML) {
    // Plugin real render captured at its natural size (scaleReal), or a schematic
    // mockup (150×110) — rendered centered and scaled to fit in onmove.
    const NAT_W = item.scaleReal ? item.natW || 300 : 150;
    const NAT_H = item.scaleReal ? item.natH || 180 : 110;
    const inner = document.createElement("div");
    Object.assign(inner.style, {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: `${NAT_W}px`,
      height: `${NAT_H}px`,
      transformOrigin: "center center",
      transform: "translate(-50%, -50%)",
      overflow: "hidden",
      opacity: "0.95",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    });
    inner.innerHTML = item.previewHTML;
    node.appendChild(inner);
    item.natW = NAT_W;
    item.natH = NAT_H;
    node._schematicInner = inner;
  } else if (item.previewHTML) {
    // Questions/audio/icons: show the card's own preview, not a blank box.
    const inner = document.createElement("div");
    Object.assign(inner.style, {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "6%",
      boxSizing: "border-box",
      opacity: "0.75",
      overflow: "hidden",
    });
    inner.innerHTML = item.previewHTML;
    inner.querySelectorAll("img, svg").forEach((el) => {
      Object.assign(el.style, {
        maxWidth: "100%",
        maxHeight: "100%",
        width: "auto",
        height: "auto",
        objectFit: "contain",
      });
    });
    node.appendChild(inner);
  }
  document.body.appendChild(node);
  return node;
};

export function initPaletteDrag() {
  if (bound) return;
  bound = true;
  interact.dynamicDrop(true);
  interact(".palette-item").draggable({
    inertia: false,
    onstart(event) {
      removeEl(GHOST_ID);
      removeEl(PREVIEW_ID);
      const rect = event.target.getBoundingClientRect();
      const ghost = event.target.cloneNode(true);
      ghost.setAttribute("id", GHOST_ID);
      ghost.setAttribute("data-x", 0);
      ghost.setAttribute("data-y", 0);
      Object.assign(ghost.style, {
        position: "fixed",
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        margin: "0",
        pointerEvents: "none",
        opacity: "0.85",
        zIndex: "100001",
        boxShadow: "0 8px 24px rgba(15,23,42,0.18)",
        transform: "translate(0px, 0px) scale(0.92)",
      });
      document.body.appendChild(ghost);
      document.body.style.cursor = "grabbing";

      const item = resolveItem(event.target);
      session = { item, preview: buildPreview(item) };
    },
    onmove(event) {
      const ghost = el(GHOST_ID);
      if (ghost) {
        const x = (parseFloat(ghost.getAttribute("data-x")) || 0) + event.dx;
        const y = (parseFloat(ghost.getAttribute("data-y")) || 0) + event.dy;
        ghost.style.transform = `translate(${x}px, ${y}px) scale(0.92)`;
        ghost.setAttribute("data-x", x);
        ghost.setAttribute("data-y", y);
      }

      if (!session) return;
      const preview = session.preview;
      const item = session.item;
      const target = canvasUnder(event.clientX, event.clientY);

      if (!target) {
        if (preview) preview.style.display = "none";
        if (ghost) ghost.style.display = "";
        return;
      }
      if (ghost) ghost.style.display = "none";
      if (!preview) return;

      const { node, rect } = target;

      // Text/smart real render at the exact on-canvas size: box width = wPct% of
      // the canvas, font = canvas font-base × slide scale, auto height. No
      // fit-to-frame — it's the real drop size. (Plugin renders use scaleReal,
      // handled by the schematic scale-to-fit branch below.)
      if (item.realPreview && !item.scaleReal && preview._realInner) {
        const inner = preview._realInner;
        const wPctR = Math.min(item.wPct, 100);
        const fwR = (wPctR / 100) * rect.width;
        const slideScale = node.offsetWidth ? rect.width / node.offsetWidth : 1;
        const designFont = parseFloat(getComputedStyle(node).fontSize) || 14;
        inner.style.width = `${fwR}px`;
        inner.style.height = "auto";
        inner.style.fontSize = `${designFont * slideScale}px`;
        preview.style.display = "block";
        const fhR = inner.offsetHeight || (Math.min(item.hPct, 100) / 100) * rect.height;
        const hPctR = Math.min((fhR / rect.height) * 100, 100);
        let rx = ((event.clientX - rect.left - node.offsetLeft) * 100) / rect.width;
        let ry = ((event.clientY - rect.top + node.scrollTop) * 100) / rect.height;
        rx = Math.max(0, Math.min(rx, 100 - wPctR));
        ry = Math.max(0, Math.min(ry, 100 - hPctR));
        preview.style.left = `${rect.left + (rx / 100) * rect.width}px`;
        preview.style.top = `${rect.top + (ry / 100) * rect.height}px`;
        preview.style.width = `${fwR}px`;
        preview.style.height = `${fhR}px`;
        return;
      }

      // Mirror EditorCanvasSli ondrop: top-left at pointer, clamped on-canvas.
      let xPct = ((event.clientX - rect.left - node.offsetLeft) * 100) / rect.width;
      let yPct = ((event.clientY - rect.top + node.scrollTop) * 100) / rect.height;
      const wPct = Math.min(item.wPct, 100);
      const hPct = Math.min(item.hPct, 100);
      xPct = Math.max(0, Math.min(xPct, 100 - wPct));
      yPct = Math.max(0, Math.min(yPct, 100 - hPct));

      const fw = (wPct / 100) * rect.width;
      const fh = (hPct / 100) * rect.height;
      preview.style.left = `${rect.left + (xPct / 100) * rect.width}px`;
      preview.style.top = `${rect.top + (yPct / 100) * rect.height}px`;
      preview.style.width = `${fw}px`;
      preview.style.height = `${fh}px`;
      preview.style.display = "block";

      // Schematic mockups scale to fit the footprint.
      if (preview._schematicInner && item.natW && item.natH) {
        const scale = Math.max(0.2, Math.min(fw / item.natW, fh / item.natH));
        preview._schematicInner.style.transform = `translate(-50%, -50%) scale(${scale})`;
      }
    },
    onend() {
      removeEl(GHOST_ID);
      removeEl(PREVIEW_ID);
      session = null;
      document.body.style.cursor = "";
    },
  });
}
